import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Pagination,
} from '@mui/material';
import { scratchCards } from '../services/api';
import GenerateCardForm from '../components/GenerateCardForm';
import ScratchCardStats from '../components/ScratchCardStats';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ScratchCardsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatedCards, setGeneratedCards] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [allScratchCards, setAllScratchCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [selectedCards, setSelectedCards] = useState([]);
  const [availableCards, setAvailableCards] = useState([]);
  
  // Filter and pagination state
  const [statusFilter, setStatusFilter] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCards, setTotalCards] = useState(0);
  const [displayedCards, setDisplayedCards] = useState([]);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [deductPoints, setDeductPoints] = useState({});
  
  // History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userHistory, setUserHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await scratchCards.getStats();
      setStats(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch statistics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllScratchCards = async (page = 1) => {
    try {
      setCardsLoading(true);
      
      // Build query parameters - fetch all records for client-side pagination
      const params = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      };
      
      const response = await scratchCards.getAll(params);
      const data = response.data || response;
      
      setAllScratchCards(data.cards || []);
      setTotalCards(data.cards?.length || 0);
      setError('');
    } catch (err) {
      setError('Failed to fetch loyalty cards');
      console.error(err);
    } finally {
      setCardsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await scratchCards.getUsers();
      setUsers(response.data || response);
      setError('');
    } catch (err) {
      setError('Failed to fetch users');
      console.error(err);
    } finally {
      setUsersLoading(false);
    }
  };

  // Set default date range (7 days ago to today)
  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    // Format dates in local timezone
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setStartDate(formatDate(sevenDaysAgo));
    setEndDate(formatDate(today));
  }, []);

  useEffect(() => {
    fetchStats();
    fetchAllScratchCards();
  }, []);

  // Refetch when filters change
  useEffect(() => {
    if (startDate && endDate) {
      setCurrentPage(1);
      fetchAllScratchCards(1);
    }
  }, [statusFilter, startDate, endDate]);

  // Clear selections when switching tabs
  useEffect(() => {
    setSelectedCards([]);
  }, [tabValue]);

  // Reset form when switching to Generate Cards tab
  useEffect(() => {
    if (tabValue === 0) {
      setGeneratedCards(null);
    }
  }, [tabValue]);

  // Fetch users when switching to Users tab
  useEffect(() => {
    if (tabValue === 2) {
      fetchUsers();
    }
  }, [tabValue]);

  // Client-side pagination
  useEffect(() => {
    if (allScratchCards.length > 0) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedCards = allScratchCards.slice(startIndex, endIndex);
      setDisplayedCards(paginatedCards);
      
      // Calculate total pages
      const totalPages = Math.ceil(allScratchCards.length / itemsPerPage);
      setTotalPages(totalPages);
    }
  }, [allScratchCards, currentPage, itemsPerPage]);

  // Update availableCards when displayedCards changes
  useEffect(() => {
    const available = displayedCards.filter(card => !card.isRedeemed);
    setAvailableCards(available);
  }, [displayedCards]);

  const handleGenerateCards = async (data) => {
    try {
      setLoading(true);
      const response = await scratchCards.generate(data);
      setGeneratedCards(response.data);
      setSuccess('Loyalty cards generated successfully!');
      await fetchStats(); // Refresh stats
      await fetchAllScratchCards(); // Refresh scratch cards list
    } catch (err) {
      setError('Failed to generate loyalty cards');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPoints = async () => {
    try {
      setLoading(true);
      await scratchCards.resetPoints();
      setSuccess('All loyalty points have been reset');
      setResetDialogOpen(false);
      await fetchStats(); // Refresh stats
    } catch (err) {
      setError('Failed to reset loyalty points');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!generatedCards) return;

    // Create CSV content
    const csvContent = 'Code,Points,Created At\n' +
      generatedCards.cards.map(card => 
        `${card.code},${card.points},${new Date(card.createdAt).toLocaleString()}`
      ).join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scratch-cards-${generatedCards.batch}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async () => {
    if (!generatedCards) return;

    try {
      setLoading(true);
      
      // Create a temporary container for the PDF content
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '210mm'; // A4 width
      tempContainer.style.padding = '20px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.backgroundColor = 'white';
      
      // Create PDF content
      tempContainer.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #1976d2; margin: 0;">Loyalty Cards - Batch ${generatedCards.batch}</h1>
          <p style="color: #666; margin: 10px 0;">Generated on ${new Date().toLocaleDateString()}</p>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px;">
          ${generatedCards.cards.map((card, index) => `
            <div style="border: 2px solid #ddd; padding: 15px; text-align: center; page-break-inside: avoid;">
              <div style="background: #f5f5f5; padding: 10px; margin-bottom: 10px; border-radius: 5px;">
                <h3 style="margin: 0; color: #333;">Loyalty Card</h3>
              </div>
              <div style="margin: 15px 0; padding: 20px; background: #f9f9f9; border-radius: 8px; border: 2px solid #333;">
                <div style="font-size: 14px; color: #666; margin-bottom: 10px;">Scratch Here</div>
                <div style="font-size: 20px; font-weight: bold; color: #000; letter-spacing: 3px; font-family: monospace; background: #fff; padding: 8px; border-radius: 4px; border: 1px solid #ccc;">
                  ${card.code}
                </div>
                <div style="font-size: 12px; color: #666; margin-top: 5px;">Cover with scratch-off layer</div>
              </div>
              <div style="margin: 10px 0;">
                <span style="background: #e3f2fd; padding: 5px 10px; border-radius: 15px; color: #1976d2;">
                  ${card.points} Points
                </span>
              </div>
              <div style="margin-top: 10px; font-size: 12px; color: #666;">
                Card #${index + 1} of ${generatedCards.cards.length}
              </div>
            </div>
          `).join('')}
        </div>
      `;
      
      document.body.appendChild(tempContainer);
      
      // Generate PDF
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Clean up
      document.body.removeChild(tempContainer);
      
      // Download PDF
      pdf.save(`scratch-cards-${generatedCards.batch}.pdf`);
      setSuccess('PDF downloaded successfully!');
      
    } catch (err) {
      setError('Failed to generate PDF');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle individual card selection
  const handleCardSelect = (cardId) => {
    setSelectedCards(prev => 
      prev.includes(cardId) 
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId]
    );
  };

  // Handle select all available cards from current page
  const handleSelectAll = (checked) => {
    const currentPageAvailableCards = displayedCards.filter(card => !card.isRedeemed);
    
    if (checked) {
      // Add current page selections
      const newSelections = currentPageAvailableCards.map(card => card.id || card.code);
      setSelectedCards(prev => [...new Set([...prev, ...newSelections])]);
    } else {
      // Remove only current page selections
      const currentPageCodes = displayedCards.map(card => card.id || card.code);
      setSelectedCards(prev => prev.filter(id => !currentPageCodes.includes(id)));
    }
  };


  // Handle printing toggle
  const handlePrintingToggle = async (cardId, checked) => {
    try {
      // Update local state immediately for better UX
      setAllScratchCards(prev => 
        prev.map(card => 
          (card.id || card.code) === cardId 
            ? { ...card, sendToPrinting: checked }
            : card
        )
      );
      
      // Update on server
      await scratchCards.updatePrintingStatus(cardId, checked);
      
      setSuccess(`Printing status updated for card ${cardId}`);
    } catch (error) {
      console.error('Error updating printing status:', error);
      setError('Failed to update printing status');
      
      // Revert local state on error
      setAllScratchCards(prev => 
        prev.map(card => 
          (card.id || card.code) === cardId 
            ? { ...card, sendToPrinting: !checked }
            : card
        )
      );
    }
  };

  // Handle sending selected cards to printing
  const handleSendToPrinting = async () => {
    if (selectedCards.length === 0) {
      setError('Please select cards to send to printing');
      return;
    }

    try {
      // Update local state immediately for better UX
      const updatedCards = allScratchCards.map(card => 
        selectedCards.includes(card.id || card.code)
          ? { ...card, sendToPrinting: true }
          : card
      );
      setAllScratchCards(updatedCards);

      // Get card codes for the selected items
      const cardCodesToUpdate = selectedCards.map(cardId => {
        const card = allScratchCards.find(c => (c.id || c.code) === cardId);
        return card ? card.code : null;
      }).filter(Boolean);

      // Use bulk update API
      await scratchCards.updateManyPrintingStatus(cardCodesToUpdate, true);
      
      setSuccess(`Sent ${selectedCards.length} cards to printing`);
    } catch (error) {
      console.error('Error sending cards to printing:', error);
      setError('Failed to send cards to printing');
      
      // Revert local state on error
      const revertedCards = allScratchCards.map(card => 
        selectedCards.includes(card.id || card.code)
          ? { ...card, sendToPrinting: false }
          : card
      );
      setAllScratchCards(revertedCards);
    }
  };

  // Handle deduct points
  const handleDeductPoints = async (userId, pointsToDeduct) => {
    // Client-side validation before API call
    if (!pointsToDeduct || pointsToDeduct <= 0) {
      setError('Please enter a valid amount to deduct');
      return;
    }

    if (pointsToDeduct < 0) {
      setError('Cannot deduct negative amounts');
      return;
    }

    // Find the user to check their current points
    const user = users.find(u => u.uid === userId);
    if (user && pointsToDeduct > (user.loyaltyPoints || 0)) {
      setError(`Cannot deduct more than ${user.loyaltyPoints || 0} points`);
      return;
    }

    try {
      setLoading(true);
      await scratchCards.deductUserPoints(userId, pointsToDeduct);
      setSuccess(`Successfully deducted ${pointsToDeduct} points from user`);
      
      // Clear the input field
      setDeductPoints(prev => ({ ...prev, [userId]: '' }));
      
      // Refresh users data
      await fetchUsers();
    } catch (error) {
      console.error('Error deducting points:', error);
      setError('Failed to deduct points');
    } finally {
      setLoading(false);
    }
  };

  // Handle opening user history dialog
  const handleOpenHistory = async (user) => {
    setSelectedUser(user);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    
    try {
      const response = await scratchCards.getUserHistory(user.uid);
      setUserHistory(response.data?.history || []);
    } catch (error) {
      console.error('Error fetching user history:', error);
      setError('Failed to fetch user history');
      setUserHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Download selected available cards as PDF
  const handleDownloadSelectedPDF = async () => {
    if (selectedCards.length === 0) {
      setError('Please select at least one card to download');
      return;
    }

    try {
      setLoading(true);
      
      const selectedCardsData = availableCards.filter(card => 
        selectedCards.includes(card.id || card.code)
      );
      
      // Create a temporary container for the PDF content
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '210mm'; // A4 width
      tempContainer.style.padding = '20px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.backgroundColor = 'white';
      
      // Create PDF content
      tempContainer.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #1976d2; margin: 0;">Available Loyalty Cards</h1>
          <p style="color: #666; margin: 10px 0;">Selected ${selectedCardsData.length} cards - Generated on ${new Date().toLocaleDateString()}</p>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px;">
          ${selectedCardsData.map((card, index) => `
            <div style="border: 2px solid #ddd; padding: 15px; text-align: center; page-break-inside: avoid;">
              <div style="background: #f5f5f5; padding: 10px; margin-bottom: 10px; border-radius: 5px;">
                <h3 style="margin: 0; color: #333;">Scratch Card</h3>
              </div>
              <div style="margin: 15px 0; padding: 20px; background: #f9f9f9; border-radius: 8px; border: 2px solid #333;">
                <div style="font-size: 14px; color: #666; margin-bottom: 10px;">Scratch Here</div>
                <div style="font-size: 20px; font-weight: bold; color: #000; letter-spacing: 3px; font-family: monospace; background: #fff; padding: 8px; border-radius: 4px; border: 1px solid #ccc;">
                  ${card.code}
                </div>
                <div style="font-size: 12px; color: #666; margin-top: 5px;">Cover with scratch-off layer</div>
              </div>
              <div style="margin: 10px 0;">
                <span style="background: #e3f2fd; padding: 5px 10px; border-radius: 15px; color: #1976d2;">
                  ${card.points} Points
                </span>
              </div>
              <div style="margin-top: 10px; font-size: 12px; color: #666;">
                Card #${index + 1} of ${selectedCardsData.length}
              </div>
            </div>
          `).join('')}
        </div>
      `;
      
      document.body.appendChild(tempContainer);
      
      // Generate PDF
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Clean up
      document.body.removeChild(tempContainer);
      
      // Download PDF
      pdf.save(`available-scratch-cards-${selectedCardsData.length}-cards.pdf`);
      setSuccess(`PDF downloaded successfully! ${selectedCardsData.length} cards included.`);
      
    } catch (err) {
      setError('Failed to generate PDF');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Loyalty Cards Management
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          {/* <Tab label="Overview" /> */}
          <Tab label="Generate Cards" />
          <Tab label="All Loyalty Cards" />
          <Tab label="Users" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {/* {tabValue === 0 && (
        <> */}
          {/* Stats Section */}
          {/* {loading && !stats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : stats ? (
            <ScratchCardStats stats={stats} />
          ) : null}
        </> */}
      {/* )} */}

      {tabValue === 0 && (
        <>
          {/* Generate Cards Section */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Generate Loyalty Cards</Typography>
              {/* <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                onClick={() => setResetDialogOpen(true)}
              >
                Reset All Points
              </Button> */}
            </Box>
            <GenerateCardForm onGenerate={handleGenerateCards} />
          </Paper>

          {/* Generated Cards Section */}
          {generatedCards && (
            <Paper sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                Successfully generated {generatedCards.quantity} loyalty cards.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleDownload}
                  >
                    Download CSV
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleDownloadPDF}
                    disabled={loading}
                  >
                    Download PDF
                  </Button>
                </Box>
              </Box>
              
              {/* Generated Cards Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Redeemed At</TableCell>
                      <TableCell>Send to Printing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {generatedCards.cards.map((card, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              fontWeight: 'bold',
                              color: '#333',
                              fontSize: '0.9rem'
                            }}
                          >
                            {card.code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${card.points} Points`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label="Available"
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date().toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            Not Redeemed
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant={card.sendToPrinting ? "contained" : "outlined"}
                              color="success"
                              startIcon={<CheckIcon />}
                              onClick={() => {
                                console.log('Printing toggle for:', card.code, true);
                                handlePrintingToggle(card.code, true);
                              }}
                              sx={{ minWidth: '60px' }}
                            >
                              Yes
                            </Button>
                            <Button
                              size="small"
                              variant={!card.sendToPrinting ? "contained" : "outlined"}
                              color="error"
                              startIcon={<CloseIcon />}
                              onClick={() => {
                                console.log('Printing toggle for:', card.code, false);
                                handlePrintingToggle(card.code, false);
                              }}
                              sx={{ minWidth: '60px' }}
                            >
                              No
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {tabValue === 1 && (
        <>
          {/* All Scratch Cards Section */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">All Loyalty Cards</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={fetchAllScratchCards}
                  disabled={cardsLoading}
                >
                  Refresh
                </Button>
                {availableCards.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      sx={{ backgroundColor: "#c1ec4b", color: "black" }}
                      variant="contained"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleDownloadSelectedPDF}
                      disabled={loading || selectedCards.length === 0}
                    >
                      Download Selected ({selectedCards.length})
                    </Button>
                    <Button
                      sx={{ backgroundColor: "#c1ec4b", color: "black" }}
                      variant="contained"
                      color="primary"
                      startIcon={<PrintIcon />}
                      onClick={handleSendToPrinting}
                      disabled={loading || selectedCards.length === 0}
                    >
                      Send To Printing ({selectedCards.length})
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Filters
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="available">Available</MenuItem>
                      <MenuItem value="redeemed">Redeemed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField 
                    fullWidth 
                    label="Items Per Page" 
                    type="number" 
                    value={itemsPerPage} 
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value && value > 0 && value <= totalCards) {
                        setItemsPerPage(value);
                      } else if (value && value > totalCards) {
                        setItemsPerPage(totalCards);
                      } else if (value && value <= 0) {
                        setItemsPerPage(1);
                      }
                    }}
                    inputProps={{ 
                      min: 1, 
                      max: totalCards || 1000,
                      step: 1 
                    }}
                    helperText={`Max: ${totalCards || 'Loading...'}`}
                    InputLabelProps={{ shrink: true }} 
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setCurrentPage(1);
                        fetchAllScratchCards(1);
                      }}
                      disabled={cardsLoading}
                      sx={{ flex: 1 }}
                    >
                      Apply Filters
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        setStatusFilter('all');
                        setItemsPerPage(20);
                        const today = new Date();
                        const sevenDaysAgo = new Date();
                        sevenDaysAgo.setDate(today.getDate() - 7);
                        const formatDate = (date) => {
                          return date.toISOString().split('T')[0];
                        };
                        setStartDate(formatDate(sevenDaysAgo));
                        setEndDate(formatDate(today));
                        setCurrentPage(1);
                        fetchAllScratchCards(1);
                      }}
                      disabled={cardsLoading}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Instructions */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Printing Instructions:</strong> The PDF shows the actual codes that will be hidden under scratch-off layers. 
                Print the cards and apply your own scratch-off coating over the code area. 
                Customers will scratch to reveal the code and enter it on your website to redeem points.
              </Typography>
            </Alert>

            {/* Available Cards Selection Toolbar */}
            {/* {availableCards.length > 0 && (
              <Toolbar sx={{ pl: 0, pr: 0, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCards.length === availableCards.length && availableCards.length > 0}
                      indeterminate={selectedCards.length > 0 && selectedCards.length < availableCards.length}
                      onChange={handleSelectAll}
                    />
                  }
                  label={`Select All Available Cards (${availableCards.length})`}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  {selectedCards.length} of {availableCards.length} selected
                </Typography>
              </Toolbar>
            )} */}
            
            {cardsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer 
                sx={{ 
                  maxHeight: 600, 
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={availableCards.length > 0 && availableCards.every(card => selectedCards.includes(card.id || card.code))}
                          indeterminate={availableCards.some(card => selectedCards.includes(card.id || card.code)) && !availableCards.every(card => selectedCards.includes(card.id || card.code))}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Redeemed At</TableCell>
                      <TableCell>Send to Printing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedCards.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="textSecondary">
                            No loyalty cards found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayedCards.map((card) => {
                        const isSelected = selectedCards.includes(card.id || card.code);
                        const isAvailable = !card.isRedeemed;
                        
                        return (
                          <TableRow key={card.id || card.code}>
                            <TableCell padding="checkbox">
                              {isAvailable && (
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleCardSelect(card.id || card.code)}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: 'monospace', 
                                  fontWeight: 'bold',
                                  color: '#333',
                                  fontSize: '0.9rem'
                                }}
                              >
                                {card.code}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={`${card.points} Points`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={card.isRedeemed ? 'Redeemed' : 'Available'}
                                size="small"
                                color={card.isRedeemed ? 'success' : 'default'}
                                variant={card.isRedeemed ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {card.createdAt ? new Date(card.createdAt).toLocaleString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {card.redeemedAt ? new Date(card.redeemedAt).toLocaleString() : 'Not Redeemed'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant={card.sendToPrinting ? "contained" : "outlined"}
                                  color="success"
                                  startIcon={<CheckIcon />}
                                  onClick={() => handlePrintingToggle(card.id || card.code, true)}
                                  disabled={card.isRedeemed}
                                  sx={{ minWidth: '60px' }}
                                >
                                  Yes
                                </Button>
                                <Button
                                  size="small"
                                  variant={!card.sendToPrinting ? "contained" : "outlined"}
                                  color="error"
                                  startIcon={<CloseIcon />}
                                  onClick={() => handlePrintingToggle(card.id || card.code, false)}
                                  disabled={card.isRedeemed}
                                  sx={{ minWidth: '60px' }}
                                >
                                  No
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => {
                    setCurrentPage(page);
                  }}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
            
            {/* Results Summary */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Showing {displayedCards.length} of {allScratchCards.length} cards (Page {currentPage} of {totalPages}, {itemsPerPage} per page)
              </Typography>
            </Box>
          </Paper>
        </>
      )}

      {tabValue === 2 && (
        <>
          {/* Users Section */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Users Loyalty Points Management</Typography>
              <Button
                variant="outlined"
                onClick={fetchUsers}
                disabled={usersLoading}
              >
                Refresh
              </Button>
            </Box>

            {usersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone Number</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Total Redeemed Points</TableCell>
                      <TableCell>Latest Redeemed At</TableCell>
                      <TableCell>Deduct Points</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="textSecondary">
                            No users found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow key={user.uid}>
                          <TableCell>
                            <Typography 
                              variant="body2"
                              sx={{ 
                                cursor: 'pointer',
                                color: 'primary.main',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => handleOpenHistory(user)}
                            >
                              {user.name || 'Unknown User'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {user.phone || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`${user.loyaltyPoints || 0} Points`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {user?.redemptionHistory?.reduce((acc, curr) => acc + (curr.points || 0), 0) || 0} Points
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {user.latestRedeemedDate 
                                ? new Date(user.latestRedeemedDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : 'Never'
                              }
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Enter points to deduct"
                              value={deductPoints[user.uid] || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow positive numbers
                                if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= (user.loyaltyPoints || 0))) {
                                  setDeductPoints(prev => ({
                                    ...prev,
                                    [user.uid]: value
                                  }));
                                }
                              }}
                              InputProps={{ 
                                inputProps: { 
                                  min: 0, 
                                  max: user.loyaltyPoints || 0 
                                } 
                              }}
                              sx={{ width: 150 }}
                              error={deductPoints[user.uid] && (
                                parseInt(deductPoints[user.uid]) < 0 || 
                                parseInt(deductPoints[user.uid]) > (user.loyaltyPoints || 0)
                              )}
                              helperText={
                                deductPoints[user.uid] && parseInt(deductPoints[user.uid]) > (user.loyaltyPoints || 0) 
                                  ? `Cannot deduct more than ${user.loyaltyPoints || 0} points`
                                  : deductPoints[user.uid] && parseInt(deductPoints[user.uid]) < 0
                                  ? 'Cannot enter negative amounts'
                                  : ''
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => handleDeductPoints(user.uid, parseInt(deductPoints[user.uid] || 0))}
                              disabled={
                                !deductPoints[user.uid] || 
                                parseInt(deductPoints[user.uid] || 0) <= 0 ||
                                parseInt(deductPoints[user.uid] || 0) > (user.loyaltyPoints || 0) ||
                                parseInt(deductPoints[user.uid] || 0) < 0
                              }
                            >
                              Deduct
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* Reset Points Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset All Loyalty Points?</DialogTitle>
        <DialogContent>
          <Typography>
            This action will reset loyalty points for all users to zero. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetPoints} color="secondary" variant="contained">
            Reset Points
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      {/* User History Dialog */}
      <Dialog 
        open={historyDialogOpen} 
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedUser ? `${selectedUser.name || 'Unknown User'}'s Redemption History` : 'User History'}
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Card Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Points</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(userHistory || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="textSecondary">
                          No history found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (userHistory || []).map((history, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {history.cardCode || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${history.points > 0 ? '+' : ''}${history.points || 0} Points`}
                            size="small"
                            color={history.points > 0 ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {history.date 
                              ? new Date(history.date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })
                              : 'N/A'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={history.status === 'Deducted' ? 'Awarded' : (history.status || 'Unknown')}
                            size="small"
                            color={history.type === 'redemption' ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ScratchCardsPage;